import { useMemo, useState } from 'react';
import type { BlockName, BlockType, CustomControlCode, StepOutput, TransitionType } from '@common/enums';
import type {
    AdminProcessActivity,
    AdminProcessProcessWorkflowStep,
    AdminProcessTask,
    IAdminProcess,
} from '@common/interfaces';
import type { MouseOverActivity, MouseOverTask } from './MouseOverStep';
import { Markers } from './Markers';
import { MouseOverStep } from './MouseOverStep';
import { Step } from './Step';
import { Transition } from './Transition';
import { WorkflowLegend } from './WorkflowLegend';

export interface IProcessWorkflowStep extends AdminProcessProcessWorkflowStep {
    processActivities?: AdminProcessActivity[];
    processTasks?: AdminProcessTask[];
}

export interface IServiceWorkflowStep {
    displayOptions: {
        x: number;
        y: number;
    };
    id: string;
    name: string;
    serviceWorkflowBlock: {
        name: BlockName;
        type: BlockType;
    };
}

export interface IServiceWorkflowTransition {
    fromStepId: string;
    id: string;
    onlyIfOutputEquals: StepOutput;
    synchronous: boolean;
    toStepId: string;
    type: TransitionType;
}

export interface IServiceActivity {
    customControlCode: CustomControlCode;
    id: string;
    label: string;
    name: string;
}

export interface IServiceWorkflowStepActivity {
    id: string;
    serviceActivityId: string;
    serviceWorkflowStepId: string;
}

interface IServiceWorkflowProps {
    process?: IAdminProcess;
    serviceActivities: IServiceActivity[];
    serviceWorkflowStepActivities: IServiceWorkflowStepActivity[];
    serviceWorkflowSteps: IServiceWorkflowStep[];
    serviceWorkflowTransitions: IServiceWorkflowTransition[];
}

const margin = 110;
const nodeHeight = 200;
const nodeWidth = 300;

function getPerformerName(task: AdminProcessTask): string {
    return task.performerPractitioner
        ? `${task.performerPractitioner.nameGiven} ${task.performerPractitioner.nameFamily}`
        : (task.performerOrganization?.name ?? '');
}

export function WorkflowGraph(props: IServiceWorkflowProps) {
    function getActivityNameForServiceWorkflowStep(serviceWorkflowStepId: string): string | undefined {
        const activityIds = props.serviceWorkflowStepActivities
            .filter((stepActivity) => stepActivity.serviceWorkflowStepId === serviceWorkflowStepId)
            .map((stepActivity) => stepActivity.serviceActivityId);
        const workflowStepActivity = props.serviceActivities.find((activity) => activityIds.includes(activity.id));

        return workflowStepActivity?.name;
    }

    const [mouseOverStep, setMouseOverStep] = useState<[IServiceWorkflowStep, IProcessWorkflowStep?]>();
    const [mouseOverServiceStep, mouseOverProcessStep] = mouseOverStep || [];

    let mouseOverActivities: MouseOverActivity[] = [];
    let mouseOverTasks: MouseOverTask[] = [];

    if (mouseOverServiceStep) {
        const activityName = getActivityNameForServiceWorkflowStep(mouseOverServiceStep.id);
        mouseOverActivities = activityName ? [{ name: activityName }] : [];
        mouseOverTasks =
            props.process?.tasks
                ?.filter((task) => task.serviceWorkflowStepId === mouseOverServiceStep.id)
                .map((task) => ({
                    performedDatetime: task.performedDatetime,
                    performerName: getPerformerName(task),
                    status: task.status,
                    type: task.type,
                })) || [];
    }

    if (mouseOverProcessStep?.processActivities?.length) {
        mouseOverActivities = mouseOverProcessStep.processActivities.map((activity) => {
            const activityName = getActivityNameForServiceWorkflowStep(mouseOverProcessStep.serviceWorkflowStepId);

            return {
                name: activityName ?? '',
                performedDatetime: activity.performedDatetime,
                status: activity.status,
            };
        });
    }

    if (mouseOverProcessStep?.processTasks?.length) {
        mouseOverTasks = mouseOverProcessStep.processTasks.map((task) => ({
            performedDatetime: task.performedDatetime,
            performerName: getPerformerName(task),
            status: task.status,
            type: task.type,
        }));
    }

    const processWorkflowSteps = useMemo(() => {
        return (
            props.process?.processWorkflowSteps.map((processWorkflowStep: IProcessWorkflowStep) => {
                const processActivities = props.process?.activities?.filter(
                    (activity) => activity.processWorkflowStepId === processWorkflowStep.id
                );
                const processTasks = props.process?.tasks?.filter(
                    (task) => task.processWorkflowStepId === processWorkflowStep.id
                );

                return {
                    ...processWorkflowStep,
                    processActivities,
                    processTasks,
                };
            }) ?? []
        );
    }, [props.process]);

    if (props.serviceWorkflowSteps.length === 0) {
        return null;
    }

    const smallestX = Math.min(...props.serviceWorkflowSteps.map((step) => step.displayOptions.x));
    const largestX = Math.max(...props.serviceWorkflowSteps.map((step) => step.displayOptions.x));
    const viewBoxWidth = (largestX - smallestX + 1) * (nodeWidth + margin);

    const smallestY = Math.min(...props.serviceWorkflowSteps.map((step) => step.displayOptions.y));
    const largestY = Math.max(...props.serviceWorkflowSteps.map((step) => step.displayOptions.y));
    const viewBoxHeight = (largestY - smallestY + 1) * (nodeHeight + margin);

    const graphSizes = {
        largestX,
        largestY,
        margin,
        nodeHeight,
        nodeWidth,
        smallestX,
        smallestY,
    };

    return (
        <>
            <WorkflowLegend />
            <svg viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}>
                <Markers />

                {props.serviceWorkflowSteps.map((step, index) => (
                    <Step
                        graphSizes={graphSizes}
                        key={index}
                        onMouseOut={() => setMouseOverStep(undefined)}
                        onMouseOver={(mouseOver) => setMouseOverStep(mouseOver)}
                        processWorkflowSteps={processWorkflowSteps
                            .filter((processWorkflowStep) => processWorkflowStep.serviceWorkflowStepId === step.id)
                            .sort(
                                (a, b) =>
                                    new Date(a.created_at ?? '').getTime() - new Date(b.created_at ?? '').getTime()
                            )}
                        serviceWorkflowStep={step}
                    />
                ))}

                {props.serviceWorkflowTransitions.map((transition, index) => {
                    const fromStep = props.serviceWorkflowSteps.find((step) => step.id === transition.fromStepId);
                    const toStep = props.serviceWorkflowSteps.find((step) => step.id === transition.toStepId);
                    const fromProcessWorkflowSteps = props.process?.processWorkflowSteps.filter(
                        (processStep) => processStep.serviceWorkflowStepId === transition.fromStepId
                    );
                    const toProcessWorkflowSteps = props.process?.processWorkflowSteps.filter(
                        (processStep) => processStep.serviceWorkflowStepId === transition.toStepId
                    );

                    return (
                        fromStep &&
                        toStep && (
                            <Transition
                                fromProcessWorkflowSteps={fromProcessWorkflowSteps}
                                fromStep={fromStep}
                                graphSizes={graphSizes}
                                key={index}
                                toProcessWorkflowSteps={toProcessWorkflowSteps}
                                toStep={toStep}
                                transition={transition}
                            />
                        )
                    );
                })}

                {mouseOverServiceStep && (
                    <MouseOverStep
                        activities={mouseOverActivities}
                        borderColor="blue"
                        borderWidth={4}
                        fillColor="white"
                        graphSizes={graphSizes}
                        processStep={mouseOverProcessStep}
                        serviceStep={mouseOverServiceStep}
                        tasks={mouseOverTasks}
                    />
                )}
            </svg>
        </>
    );
}
